// Docs: https://www.instantdb.com/docs/modeling-data
// AgentFramer

import { i } from "@instantdb/react";

const _schema = i.schema({
	entities: {
		$files: i.entity({
			path: i.string().unique().indexed(),
			url: i.string(),
		}),
		$users: i.entity({
			email: i.string().unique().indexed().optional(),
			imageURL: i.string().optional(),
			type: i.string().optional(),
		}),
		accounts: i.entity({
			accessToken: i.string().optional(),
			accessTokenExpiresAt: i.date().optional(),
			accountId: i.string().indexed(),
			createdAt: i.date(),
			idToken: i.string().optional(),
			password: i.string().optional(),
			providerId: i.string().indexed(),
			refreshToken: i.string().optional(),
			refreshTokenExpiresAt: i.date().optional(),
			scope: i.string().optional(),
			updatedAt: i.date(),
			userId: i.string().indexed(),
		}),
		sessions: i.entity({
			createdAt: i.date(),
			expiresAt: i.date(),
			impersonatedBy: i.string().optional(),
			ipAddress: i.string().optional(),
			token: i.string().unique().indexed(),
			updatedAt: i.date(),
			userAgent: i.string().optional(),
			userId: i.string().indexed(),
		}),
		users: i.entity({
			agentLastSeenAt: i.date().optional(),
			createdAt: i.date(),
			banned: i.boolean().optional(),
			banExpires: i.date().optional(),
			banReason: i.string().optional(),
			email: i.string().unique().indexed(),
			emailVerified: i.boolean().optional(),
			image: i.string().optional(),
			notifyOnJobCompletionEmail: i.boolean().optional(),
			name: i.string().indexed().optional(),
			onboardedAt: i.date().optional(),
			role: i.string().optional(),
			signupMethod: i.string().indexed().optional(),
			updatedAt: i.date(),
		}),
		workspaces: i.entity({
			name: i.string().indexed(),
			slug: i.string().unique().indexed(),
			balanceCents: i.number().optional(),
			stripeCustomerId: i.string().indexed().optional(),
			createdAt: i.date(),
			updatedAt: i.date(),
		}),
		workspaceMembers: i.entity({
			role: i.string().indexed(),
			isActive: i.boolean().indexed(),
			joinedAt: i.date(),
		}),
		agentMcpKeys: i.entity({
			createdAt: i.date(),
			keyHash: i.string().unique().indexed(),
			keyPrefix: i.string().indexed(),
			lastUsedAt: i.date().optional(),
			name: i.string().optional(),
			ownerKey: i.string().indexed(),
			revokedAt: i.date().optional(),
			updatedAt: i.date(),
		}),
		verifications: i.entity({
			createdAt: i.date().indexed().optional(),
			expiresAt: i.date().indexed(),
			identifier: i.string().indexed(),
			updatedAt: i.date().optional(),
			value: i.string(),
		}),
		chatConversations: i.entity({
			createdAt: i.string(),
			updatedAt: i.string(),
			status: i.string().indexed().optional(),
			lastMessagePreview: i.string().optional(),
			unreadCount: i.number().optional(),
		}),
		chatMessages: i.entity({
			content: i.string(),
			createdAt: i.string(),
			updatedAt: i.string().optional(),
			senderType: i.string().indexed(),
			isRead: i.boolean().optional(),
		}),
		assistantThreads: i.entity({
			createdAt: i.date(),
			lastMessagePreview: i.string().optional(),
			status: i.string().indexed().optional(),
			title: i.string().optional(),
			updatedAt: i.date(),
		}),
		assistantMessages: i.entity({
			content: i.string().optional(),
			createdAt: i.date(),
			role: i.string().indexed(),
		}),
		assistantToolCalls: i.entity({
			argumentsJson: i.string(),
			cancelledAt: i.date().optional(),
			confirmedAt: i.date().optional(),
			createdAt: i.date(),
			error: i.string().optional(),
			name: i.string().indexed(),
			requiresConfirmation: i.boolean().optional(),
			responseJson: i.string().optional(),
			status: i.string().indexed(),
			updatedAt: i.date(),
		}),
		feedback: i.entity({
			category: i.string().indexed().optional(),
			contactEmail: i.string().optional(),
			createdAt: i.date(),
			message: i.string(),
			userEmail: i.string().optional(),
			userName: i.string().optional(),
		}),
		generations: i.entity({
			createdAt: i.date(),
			error: i.string().optional(),
			generationType: i.string().indexed(),
			mimeType: i.string().optional(),
			model: i.string().optional(),
			outputStorageKey: i.string().optional(),
			outputUrl: i.string().optional(),
			prompt: i.string().optional(),
			providerJobId: i.string().indexed().optional(),
			sourceUrl: i.string().optional(),
			status: i.string().indexed(),
			title: i.string().optional(),
			updatedAt: i.date(),
		}),
		billingTransactions: i.entity({
			amountCents: i.number(),
			createdAt: i.date(),
			description: i.string().optional(),
			generationId: i.string().unique().indexed().optional(),
			provider: i.string().indexed(),
			stripeCheckoutSessionId: i.string().unique().indexed().optional(),
			transactionType: i.string().indexed(),
		}),
		pendingTopUps: i.entity({
			amountCents: i.number(),
			claimedAt: i.date().optional(),
			createdAt: i.date(),
			email: i.string().indexed(),
			stripeCheckoutSessionId: i.string().unique().indexed(),
			stripeCustomerId: i.string().indexed().optional(),
			updatedAt: i.date(),
		}),
		workspaceInvitations: i.entity({
			email: i.string().indexed(),
			token: i.string().unique().indexed(),
			role: i.string(),
			expiresAt: i.date(),
			createdAt: i.date(),
		}),
		cliAuthCodes: i.entity({
			code: i.string().unique().indexed(),
			status: i.string().indexed(),
			expiresAt: i.date(),
			createdAt: i.date(),
			apiKey: i.string().optional(),
		}),
	},
	links: {
		$usersLinkedPrimaryUser: {
			forward: {
				on: "$users",
				has: "one",
				label: "linkedPrimaryUser",
				onDelete: "cascade",
			},
			reverse: {
				on: "$users",
				has: "many",
				label: "linkedGuestUsers",
			},
		},
		accountsUser: {
			forward: {
				on: "accounts",
				has: "one",
				label: "user",
				onDelete: "cascade",
			},
			reverse: {
				on: "users",
				has: "many",
				label: "accounts",
			},
		},
		sessionsUser: {
			forward: {
				on: "sessions",
				has: "one",
				label: "user",
				onDelete: "cascade",
			},
			reverse: {
				on: "users",
				has: "many",
				label: "sessions",
			},
		},
		agentMcpKeysUser: {
			forward: {
				on: "agentMcpKeys",
				has: "one",
				label: "user",
				onDelete: "cascade",
			},
			reverse: {
				on: "users",
				has: "many",
				label: "agentMcpKeys",
			},
		},
		users$user: {
			forward: {
				on: "users",
				has: "one",
				label: "$user",
				onDelete: "cascade",
			},
			reverse: {
				on: "$users",
				has: "one",
				label: "user",
			},
		},
		chatConversationsUser: {
			forward: {
				on: "chatConversations",
				has: "one",
				label: "user",
			},
			reverse: {
				on: "users",
				has: "one",
				label: "chatConversation",
			},
		},
		chatMessagesConversation: {
			forward: {
				on: "chatMessages",
				has: "one",
				label: "conversation",
				onDelete: "cascade",
			},
			reverse: {
				on: "chatConversations",
				has: "many",
				label: "messages",
			},
		},
		chatMessagesSender: {
			forward: {
				on: "chatMessages",
				has: "one",
				label: "sender",
			},
			reverse: {
				on: "users",
				has: "many",
				label: "chatMessages",
			},
		},
		assistantThreadsUser: {
			forward: {
				on: "assistantThreads",
				has: "one",
				label: "user",
				onDelete: "cascade",
			},
			reverse: {
				on: "users",
				has: "many",
				label: "assistantThreads",
			},
		},
		assistantThreadsWorkspace: {
			forward: {
				on: "assistantThreads",
				has: "one",
				label: "workspace",
				onDelete: "cascade",
			},
			reverse: {
				on: "workspaces",
				has: "many",
				label: "assistantThreads",
			},
		},
		assistantMessagesThread: {
			forward: {
				on: "assistantMessages",
				has: "one",
				label: "thread",
				onDelete: "cascade",
			},
			reverse: {
				on: "assistantThreads",
				has: "many",
				label: "messages",
			},
		},
		assistantToolCallsThread: {
			forward: {
				on: "assistantToolCalls",
				has: "one",
				label: "thread",
				onDelete: "cascade",
			},
			reverse: {
				on: "assistantThreads",
				has: "many",
				label: "toolCalls",
			},
		},
		workspaceMembersUser: {
			forward: {
				on: "workspaceMembers",
				has: "one",
				label: "user",
				onDelete: "cascade",
			},
			reverse: {
				on: "users",
				has: "many",
				label: "workspaceMembers",
			},
		},
		workspaceMembersWorkspace: {
			forward: {
				on: "workspaceMembers",
				has: "one",
				label: "workspace",
				onDelete: "cascade",
			},
			reverse: {
				on: "workspaces",
				has: "many",
				label: "members",
			},
		},
		agentMcpKeysWorkspace: {
			forward: {
				on: "agentMcpKeys",
				has: "one",
				label: "workspace",
				onDelete: "cascade",
			},
			reverse: {
				on: "workspaces",
				has: "many",
				label: "agentMcpKeys",
			},
		},
		chatConversationsWorkspace: {
			forward: {
				on: "chatConversations",
				has: "one",
				label: "workspace",
			},
			reverse: {
				on: "workspaces",
				has: "one",
				label: "chatConversation",
			},
		},
		feedbackUser: {
			forward: {
				on: "feedback",
				has: "one",
				label: "user",
				onDelete: "cascade",
			},
			reverse: {
				on: "users",
				has: "many",
				label: "feedback",
			},
		},
		generationsUser: {
			forward: {
				on: "generations",
				has: "one",
				label: "user",
				onDelete: "cascade",
			},
			reverse: {
				on: "users",
				has: "many",
				label: "generations",
			},
		},
		generationsWorkspace: {
			forward: {
				on: "generations",
				has: "one",
				label: "workspace",
				onDelete: "cascade",
			},
			reverse: {
				on: "workspaces",
				has: "many",
				label: "generations",
			},
		},
		billingTransactionsUser: {
			forward: {
				on: "billingTransactions",
				has: "one",
				label: "user",
				onDelete: "cascade",
			},
			reverse: {
				on: "users",
				has: "many",
				label: "billingTransactions",
			},
		},
		billingTransactionsWorkspace: {
			forward: {
				on: "billingTransactions",
				has: "one",
				label: "workspace",
				onDelete: "cascade",
			},
			reverse: {
				on: "workspaces",
				has: "many",
				label: "billingTransactions",
			},
		},
		workspaceInvitationsWorkspace: {
			forward: {
				on: "workspaceInvitations",
				has: "one",
				label: "workspace",
				onDelete: "cascade",
			},
			reverse: {
				on: "workspaces",
				has: "many",
				label: "invitations",
			},
		},
		workspaceInvitationsInviter: {
			forward: {
				on: "workspaceInvitations",
				has: "one",
				label: "inviter",
			},
			reverse: {
				on: "users",
				has: "many",
				label: "sentInvitations",
			},
		},
		cliAuthCodesUser: {
			forward: {
				on: "cliAuthCodes",
				has: "one",
				label: "user",
				onDelete: "cascade",
			},
			reverse: {
				on: "users",
				has: "many",
				label: "cliAuthCodes",
			},
		},
		cliAuthCodesWorkspace: {
			forward: {
				on: "cliAuthCodes",
				has: "one",
				label: "workspace",
				onDelete: "cascade",
			},
			reverse: {
				on: "workspaces",
				has: "many",
				label: "cliAuthCodes",
			},
		},
	},
	rooms: {},
});

type _AppSchema = typeof _schema;
interface AppSchema extends _AppSchema {}
const schema: AppSchema = _schema;

export type { AppSchema };
export default schema;
